"use client";

/**
 * 关键指标统计卡（2026-10-08）。
 *
 * ## 🔴 每张卡强制带「口径」，且可展开
 *
 * 「总数 17」这个数字**无法自解释**：17 个什么？含不含已删除？
 * 按创建时间还是更新时间？—— 不同口径能差出一倍。
 * 所以每张卡的 `scope.source`（数据表）与 `scope.rule`（统计规则）
 * 收进卡上的 ⓘ 按钮：点开即见「来源 · 规则」，默认不占视野
 * （首版来源表名常驻被用户反馈为技术噪音，2026-10-08 收起）。
 *
 * 这不是文档洁癖：本项目此前多次出现「页头说 18 个、卡片加起来 17 个」
 * 的不一致，用户报障后排查半天才发现是两处口径不同。
 *
 * ## 🔴 服务端给的键是**根路径**，必须用根翻译器解析
 *
 * `metric.labelKey` = `home.metrics.projectsTotal`（含命名空间）。
 * 若用 `useTranslations("home")` 拿到的 `t()` 去解析，会得到
 * `home.home.metrics.projectsTotal` —— next-intl 找不到就**原样返回键名**，
 * 界面上直接印出一串 `home.home.metrics.projectsTotal`。
 *
 * 这个 bug 极隐蔽：tsc 全绿、门禁全绿、页面 200，只有把 HTML 抓下来看
 * 才发现文案全是键名。故凡是渲染**数据层给的键**，一律用 `useTranslations()`
 * （无参 = 根作用域），本组件自己的字面量文案才用 `useTranslations("home")`。
 *
 * ## 涨跌配色遵循 A 股约定（涨红跌绿）
 * 本项目面向中文用户，指标涨跌沿用「红涨绿跌」，与欧美 UI 的「绿=好」相反。
 */

import { useState } from "react";
import { useTranslations } from "next-intl";
import { ArrowDownRight, ArrowUpRight, Info, Minus } from "lucide-react";

import { F, FOCUS } from "@/components/studio/ui";
import { relativeTimeOf } from "@/lib/project-stats";
import type { HomeBoard, HomeMetric } from "@/lib/home/types";

import { HomeSection } from "./home-primitives";

/** 大数字缩写：12345 → 12.3k。指标卡宽度有限，四位数以上必须缩。 */
function abbr(n: number): string {
  if (Math.abs(n) < 1000) return String(n);
  if (Math.abs(n) < 1_000_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
}

/** 环比徽标。`deltaPct === null` = 无可比基期，显「—」而不是 +0% 或 +∞%。 */
function DeltaBadge({ metric }: { metric: HomeMetric }) {
  // 🔴 根翻译器：metric.trend 判据在数据层，标签「较昨日/无基期」是本组件自有文案 → home 命名空间
  const t = useTranslations("home");
  const { deltaPct, trend } = metric;

  if (deltaPct === null) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] tabular-nums text-[var(--text-muted)]">
        <Minus className="h-3 w-3" aria-hidden />
        {t("delta.none")}
      </span>
    );
  }

  // 🔴 红涨绿跌（A 股约定），与欧美 UI 相反
  const up = trend === "up";
  const flat = trend === "flat";
  const cls = flat
    ? "text-[var(--text-muted)]"
    : up
      ? "text-[var(--destructive)]"
      : "text-[var(--success)]";
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;

  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] tabular-nums ${cls}`}>
      <Icon className="h-3 w-3" aria-hidden />
      {`${Math.abs(deltaPct)}%`}
    </span>
  );
}

function MetricCard({ metric }: { metric: HomeMetric }) {
  const t = useTranslations("home");
  // 🔴 根翻译器（无参）：metric.labelKey / unitKey / scope.rule 都带 `home.` 前缀，
  // 用命名空间翻译器会解析成 `home.home.xxx` 并把键名原样印到界面。
  const tRoot = useTranslations();
  const [open, setOpen] = useState(false);

  return (
    <div
      className="group/metric relative flex min-w-0 flex-col gap-1 rounded-xl border border-[var(--border-subtle)] bg-white px-3 py-2.5 transition-colors hover:border-[var(--primary)]/35"
      data-metric={metric.id}
    >
      {/* 标签行：标签 + 口径 ⓘ。
          🔴 标签用 F.label（12px secondary，5.37:1）而**不是** F.deco：
          F.deco 是 11px + --text-muted（实测 2.63:1，WCAG AA 正文需 4.5:1），
          而 studio/ui.ts 文件头已明确「muted 不得用于任何承载信息的文字」。
          「项目数」三个字是这张卡的主标签，用 muted 承载是**规范自相矛盾**。
          —— 用户说的「排版不统一」有一半来自此：标签弱到读不清，
          眼睛只能抓住右边的大数字，卡片读不出「这是个什么数」。 */}
      <div className="flex min-w-0 items-center gap-1">
        <span className={`${F.label} min-w-0 truncate`}>{tRoot(metric.labelKey)}</span>
        {/* 口径说明：点开看规则。🔴 不是 title 属性 ——
            触屏没有 hover，title 里的关键信息等于不存在。 */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={t("metric.scopeToggle")}
          className={`shrink-0 rounded p-0.5 text-[var(--text-muted)] opacity-60 transition-all hover:bg-[var(--surface)] hover:text-[var(--text-secondary)] group-hover/metric:opacity-100 ${FOCUS}`}
        >
          <Info className="h-3 w-3" aria-hidden />
        </button>
      </div>

      {/* 数字行：数字 + 单位（左侧）｜环比徽标 + 「较昨日」（右侧）。
          🔴 改竖排（标签在上、数字在下）而非首版的「标签左·数字右」两端对齐：
          两端对齐时各卡会留约 60px 空洞（1440px 实测），数字与标签之间的
          空白把二者拆成两个孤立元素，读起来像两张卡拼在一行。
          竖排让「标签→数值」成为一个自然的视觉块，且不依赖卡宽。
          🔴 第 6 轮把「较昨日」从**独立第三行**并进本行右端：
          首版每张卡三行（标签 / 数字 / 较昨日），6 张卡两行排布时高度
          ≈190px，比旁边 112px 的趋势图高出近一倍，整行视觉重心偏左。
          「较昨日」这三个字在 deltaPct === null 时整块不渲染 ——
          同一句噪音重复 6 遍，是首版「顶部太松散」的主因，现在它只在
          **有环比的那几张卡**上出现一次，与徽标同行不额外占高度。 */}
      <div className="flex min-w-0 items-baseline gap-1">
        <span className="text-[26px] leading-none font-semibold tracking-tight tabular-nums text-[var(--text-primary)]">
          {abbr(metric.value)}
        </span>
        {metric.unitKey ? (
          <span className={`${F.deco} shrink-0`}>{tRoot(metric.unitKey)}</span>
        ) : null}
        {metric.deltaPct !== null ? (
          <span className="ml-auto flex shrink-0 items-baseline gap-1">
            <DeltaBadge metric={metric} />
            <span className={`${F.deco} hidden sm:inline`}>{t("delta.vsYesterday")}</span>
          </span>
        ) : null}
      </div>

      {/* 口径说明（来源表 + 统计规则）：默认收起，点 ⓘ 展开。
          🔴 首版「来源表名常驻」被用户反馈为视觉噪音（`sqlite` 等技术细节
          对浏览数据的用户没有信息量，却让每张卡多一行 monospace）——
          口径信息保留但按需展示：一键可达，不占默认视野。 */}
      {open ? (
        <p className={`${F.deco} truncate border-t border-[var(--border-subtle)] pt-1 font-mono`}>
          {metric.scope.source} · {tRoot(metric.scope.rule)}
        </p>
      ) : null}
    </div>
  );
}

/**
 * 指标卡网格。
 *
 * 🔴 6 张卡**恒 2 列 × 3 行**（2026-10-08 第 9 轮起）：
 * 第 6 轮是「移动 2 / 平板 3 / 桌面 3」，建立在指标区占半栏（≈680px）之上，
 * 每张 ≈220px。第 9 轮占比卡并入总览行后指标区收窄到 1/3（≈364px），
 * 3 列每张只剩 ≈110px —— 内容宽「26px 数字 + 单位 + 环比徽标」实测 ≈109px，
 * 必然溢出/截断。2 列在 364px 下每张 ≈170px，从手机（≈165px）到 2k
 * （≈240px）全程装得下，**卡内排版一个字都不用改**。
 *
 * 🔴 容器底色用 `--surface`（浅灰）而非纯白：
 * 页面其它区块都是白卡，若指标区也是白卡，6 张卡 + 指标区外框 =
 * **七重同色矩形嵌套**，视觉上糊成一片（用户截图里指标条就是这种感觉：
 * 看不出「这是一组数字」，只看到一堆边框）。
 * 浅灰底 + 白卡形成一层明确的「托起」关系，指标区才读得出是一个整体。
 *
 * 🔴 高度差消化（2026-10-09 纵向对齐改版）：总览行 stretch 对齐底边，本卡常常不是最高的一张。
 *    旧做法是 `content-center` 让网格在多余高度里**居中** —— 症状是三张卡的
 *    **第一行内容不在同一条水平线上**（每张卡各自上下均分，留白不同）；
 *    纵向对齐看的正是这条线，而不是底边。
 *    现在改成：正文**顶对齐** + 网格 `auto-rows-fr` 把多余高度均分给 6 张卡本身
 *    （卡片变高，而不是在网格上下留白）。三张卡首行同线，且本卡内部不出现空洞。
 */
export function MetricBoard({ board }: { board: HomeBoard }) {
  const t = useTranslations("home");

  // 标题栏不再常驻「数据源: sqlite」：表名是技术噪音（2026-10-08 用户反馈），
  // 口径已收进每张卡的 ⓘ；页脚 scopeNote 统一交代数据来源。
  return (
    <HomeSection
      title={t("metrics.title")}
      hint={t("metrics.hint")}
      tone="sunken"
      bodyClassName="flex flex-col"
    >
      <div className="grid flex-1 auto-rows-fr grid-cols-2 gap-1.5">
        {board.metrics.map((m) => (
          <MetricCard key={m.id} metric={m} />
        ))}
      </div>
    </HomeSection>
  );
}
